import React from 'react';
import { Button, Form, FormControl } from 'react-bootstrap';
import { Navigate } from 'react-router';
import { FiChevronDown, FiChevronRight, FiFileText, FiList, FiPlusSquare, FiRefreshCw, FiSearch, FiTag } from 'react-icons/fi';
import moment from 'moment';
import config from '../../../config';
import { canAccessEngineeringModule } from '../../utils/roleConstants';

const revisionTypeLabels = {
    PART_NO: 'Part No',
    DRAWING: 'Drawing',
    BOM: 'BOM',
    NEW_PART: 'New Part'
};

const bomFieldLabels = {
    material: 'Part No',
    materialDesc: 'Description',
    color: 'Color',
    code: 'Code',
    usage: 'Usage',
    uom: 'UoM',
    per: 'Per',
    where_use: 'Where Use',
    source: 'Source',
    category: 'Category',
    supplier: 'Supplier',
    parent_bom_id: 'Parent BOM'
};

const newPartFieldLabels = {
    partType: 'Part Type',
    partNoPrefix: 'Part No Prefix',
    partNo: 'Part No',
    fullPartNo: 'Full Part No',
    customerPartNo: 'Customer Part No',
    supplierPartNo: 'Supplier Part No',
    description: 'Description',
    isObsolete: 'Status'
};

export class RevisionHistory extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            histories: [],
            expandedGroups: {},
            keyword: '',
            revisionType: 'ALL',
            loading: false,
            currentPage: 1,
            rowsPerPage: 100,
            totalRows: 0
        };
    }

    componentDidMount() {
        this.loadHistory();
    }

    safeString(value) {
        if (value === null || value === undefined) return '';
        const text = String(value);
        return text === 'null' || text === 'undefined' ? '' : text;
    }

    parseDrawingFiles(value) {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }

    parseBomItem(value) {
        if (!value) return null;
        if (typeof value === 'object') return value;
        try {
            return JSON.parse(value);
        } catch (error) {
            return null;
        }
    }

    parseNewPartItem(value) {
        if (!value) return null;
        if (typeof value === 'object') return value;
        try {
            return JSON.parse(value);
        } catch (error) {
            return null;
        }
    }

    getBomLabel(item) {
        if (!item) return 'BOM item';
        return this.safeString(item.material || item.materialDesc || item.code) || 'BOM item';
    }

    getNewPartLabel(item) {
        if (!item) return 'New Part';
        return this.safeString(item.fullPartNo || item.partNo || item.description) || 'New Part';
    }

    getNewPartStatusLabel(item) {
        return item && item.isObsolete ? 'Obsolete' : 'Active';
    }

    getDrawingFileUrl(file) {
        if (!file) return '';
        const rawPath = file.imgPath || file.url || file.path || '';
        if (!rawPath) return '';
        if (/^https?:\/\//i.test(rawPath)) return rawPath;
        const cleanPath = rawPath.replace(/^\/+/, '');
        if (cleanPath.indexOf('img/') === 0) return cleanPath;
        return `img/${cleanPath.split(/[\\/]/).pop()}`;
    }

    loadHistory(page = this.state.currentPage) {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) return Promise.resolve();

        const offset = (page - 1) * this.state.rowsPerPage;
        const params = new URLSearchParams({
            company: user.accountId,
            keyword: this.state.keyword || '',
            revisionType: this.state.revisionType,
            limit: this.state.rowsPerPage,
            offset
        });

        this.setState({ loading: true });

        return fetch(`${config.apiUrl}/getRevisionHistory?${params.toString()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.text().then(text => text && JSON.parse(text)))
            .then(body => {
                const histories = body && Array.isArray(body.data) ? body.data : [];
                const totalRows = body && body.meta && body.meta.total_count ? body.meta.total_count : histories.length;
                this.setState({
                    histories,
                    totalRows,
                    currentPage: page,
                    loading: false
                });
            })
            .catch(() => {
                this.setState({ histories: [], totalRows: 0, loading: false });
            });
    }

    toggleGroup(groupKey) {
        this.setState(prev => ({
            expandedGroups: {
                ...prev.expandedGroups,
                [groupKey]: !prev.expandedGroups[groupKey]
            }
        }));
    }

    getGroupedHistories() {
        const grouped = {};

        this.state.histories.forEach(item => {
            const masterData = item.masterData || {};
            const isNewPart = item.revisionType === 'NEW_PART';
            const latestNewPart = isNewPart
                ? (this.parseNewPartItem(item.newNewPartData) || this.parseNewPartItem(item.previousNewPartData))
                : null;
            const groupKey = isNewPart
                ? `new-part-${item.partDataId || item.id}`
                : String(item.masterDataId || item.id);

            if (!grouped[groupKey]) {
                grouped[groupKey] = {
                    key: groupKey,
                    kind: isNewPart ? 'NEW_PART' : 'MASTER_DATA',
                    masterData,
                    currentPartNo: isNewPart
                        ? this.getNewPartLabel(latestNewPart)
                        : this.safeString(masterData.partNo || item.newPartNo || item.previousPartNo) || '-',
                    currentPartDesc: isNewPart ? this.safeString(latestNewPart && latestNewPart.description) : this.safeString(masterData.partDesc) || '',
                    currentCustomer: this.safeString(masterData.customer) || '',
                    currentDrawingNo: this.safeString(masterData.drawingNo || item.newDrawingNo || item.previousDrawingNo) || '-',
                    currentDrawingRevision: this.safeString(masterData.drawingRevision || item.newDrawingRevision || item.previousDrawingRevision) || '-',
                    currentDrawingFiles: this.parseDrawingFiles(masterData.drawingFiles || item.newDrawingFiles),
                    currentNewPartType: isNewPart ? this.safeString(latestNewPart && latestNewPart.partType) : '',
                    currentNewPartStatus: isNewPart ? this.getNewPartStatusLabel(latestNewPart) : '',
                    latestEditedAt: item.editedAt || item.createdAt,
                    histories: []
                };
            }

            grouped[groupKey].histories.push(item);
        });

        return Object.values(grouped).map(group => ({
            ...group,
            histories: group.histories.sort((a, b) => {
                const first = new Date(b.editedAt || b.createdAt || 0).getTime();
                const second = new Date(a.editedAt || a.createdAt || 0).getTime();
                return first - second;
            })
        })).sort((a, b) => {
            const first = new Date(b.latestEditedAt || 0).getTime();
            const second = new Date(a.latestEditedAt || 0).getTime();
            return first - second;
        });
    }

    renderFileList(title, files) {
        return (
            <div style={styles.fileColumn}>
                <div style={styles.fileColumnTitle}>{title}</div>
                {files.length === 0 ? (
                    <div style={styles.emptyFiles}>No drawing file</div>
                ) : (
                    files.map((file, index) => {
                        const href = this.getDrawingFileUrl(file);
                        return (
                            <a
                                key={`${file.filename || file.originalname || 'drawing'}-${index}`}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.fileLink}
                            >
                                <FiFileText />
                                <span>
                                    {file.originalname || file.filename || `Drawing ${index + 1}`}
                                    {file.drawingRevision ? ` - Rev ${file.drawingRevision}` : ''}
                                </span>
                            </a>
                        );
                    })
                )}
            </div>
        );
    }

    getFileIdentity(file) {
        if (!file) return '';
        return this.safeString(file.imgPath || file.filename || file.originalname || file.url || file.path);
    }

    getFileLabel(file, fallbackIndex) {
        if (!file) return `Drawing ${fallbackIndex + 1}`;
        return this.safeString(file.originalname || file.filename || file.imgPath) || `Drawing ${fallbackIndex + 1}`;
    }

    getFileChanges(previousFiles, newFiles) {
        const previousKeys = new Set(previousFiles.map(file => this.getFileIdentity(file)).filter(Boolean));
        const newKeys = new Set(newFiles.map(file => this.getFileIdentity(file)).filter(Boolean));
        const addedFiles = newFiles.filter(file => {
            const key = this.getFileIdentity(file);
            return key && !previousKeys.has(key);
        });
        const removedFiles = previousFiles.filter(file => {
            const key = this.getFileIdentity(file);
            return key && !newKeys.has(key);
        });
        return { addedFiles, removedFiles };
    }

    joinSentenceParts(parts, keyPrefix) {
        if (parts.length === 0) return '';
        return parts.map((part, index) => (
            <React.Fragment key={`${keyPrefix}-${index}`}>
                {index > 0 && (index === parts.length - 1 ? ' and ' : ', ')}
                {part}
            </React.Fragment>
        ));
    }

    renderChangedField(label, previousValue, newValue, key) {
        return (
            <React.Fragment key={key}>
                {label} from <span style={styles.diffRemoved}>{this.safeString(previousValue) || '-'}</span> to <span style={styles.diffAdded}>{this.safeString(newValue) || '-'}</span>
            </React.Fragment>
        );
    }

    renderFileChangeSummary(previousFiles, newFiles) {
        const { addedFiles, removedFiles } = this.getFileChanges(previousFiles, newFiles);
        const parts = [];

        if (addedFiles.length > 0) {
            parts.push(
                <React.Fragment key="added-files">
                    added <span style={styles.diffAdded}>{addedFiles.map((file, index) => this.getFileLabel(file, index)).join(', ')}</span>
                </React.Fragment>
            );
        }

        if (removedFiles.length > 0) {
            parts.push(
                <React.Fragment key="removed-files">
                    removed <span style={styles.diffRemoved}>{removedFiles.map((file, index) => this.getFileLabel(file, index)).join(', ')}</span>
                </React.Fragment>
            );
        }

        if (parts.length === 0 && previousFiles.length !== newFiles.length) {
            parts.push(
                <React.Fragment key="file-count">
                    changed file count from <span style={styles.diffRemoved}>{previousFiles.length}</span> to <span style={styles.diffAdded}>{newFiles.length}</span>
                </React.Fragment>
            );
        }

        return parts.length > 0 ? (
            <React.Fragment>
                drawing files ({this.joinSentenceParts(parts, 'file-change')})
            </React.Fragment>
        ) : null;
    }

    renderRevisionSentence(item, previousFiles, newFiles) {
        const editor = this.safeString(item.editedByName) || 'Unknown';

        if (item.revisionType === 'PART_NO') {
            return (
                <>
                    <span style={styles.editorName}>{editor}</span>
                    <span> changed {this.renderChangedField('Part No', item.previousPartNo, item.newPartNo, 'part-no')}</span>
                </>
            );
        }

        if (item.revisionType === 'BOM') {
            return this.renderBomRevisionSentence(item, editor);
        }

        if (item.revisionType === 'NEW_PART') {
            return this.renderNewPartRevisionSentence(item, editor);
        }

        const changes = [];
        const previousDrawingNo = this.safeString(item.previousDrawingNo) || '-';
        const newDrawingNo = this.safeString(item.newDrawingNo) || '-';
        const previousDrawingRevision = this.safeString(item.previousDrawingRevision) || '-';
        const newDrawingRevision = this.safeString(item.newDrawingRevision) || '-';
        const fileSummary = this.renderFileChangeSummary(previousFiles, newFiles);

        if (previousDrawingNo !== newDrawingNo) {
            changes.push(this.renderChangedField('Drawing No', previousDrawingNo, newDrawingNo, 'drawing-no'));
        }

        if (previousDrawingRevision !== newDrawingRevision) {
            changes.push(this.renderChangedField('Drawing Revision', previousDrawingRevision, newDrawingRevision, 'drawing-revision'));
        }

        if (fileSummary) {
            changes.push(fileSummary);
        }

        return (
            <>
                <span style={styles.editorName}>{editor}</span>
                <span> updated {changes.length > 0 ? this.joinSentenceParts(changes, 'drawing-change') : 'the drawing record'}</span>
            </>
        );
    }

    getNewPartFieldValue(item, field) {
        if (!item) return '';
        if (field === 'isObsolete') return this.getNewPartStatusLabel(item);
        return this.safeString(item[field]);
    }

    renderNewPartRevisionSentence(item, editor) {
        const previousItem = this.parseNewPartItem(item.previousNewPartData);
        const newItem = this.parseNewPartItem(item.newNewPartData);
        const action = this.safeString(item.newPartAction).toUpperCase();

        if (action === 'CREATE') {
            return (
                <>
                    <span style={styles.editorName}>{editor}</span>
                    <span> created New Part <span style={styles.diffAdded}>{this.getNewPartLabel(newItem)}</span></span>
                </>
            );
        }

        if (action === 'OBSOLETE') {
            return (
                <>
                    <span style={styles.editorName}>{editor}</span>
                    <span> marked New Part <span style={styles.diffNeutral}>{this.getNewPartLabel(newItem || previousItem)}</span> as <span style={styles.diffRemoved}>Obsolete</span></span>
                </>
            );
        }

        if (action === 'ACTIVATE') {
            return (
                <>
                    <span style={styles.editorName}>{editor}</span>
                    <span> marked New Part <span style={styles.diffNeutral}>{this.getNewPartLabel(newItem || previousItem)}</span> as <span style={styles.diffAdded}>Active</span></span>
                </>
            );
        }

        const changes = Object.keys(newPartFieldLabels).reduce((parts, field) => {
            const previousValue = this.getNewPartFieldValue(previousItem, field);
            const newValue = this.getNewPartFieldValue(newItem, field);

            if (previousValue !== newValue) {
                parts.push(this.renderChangedField(newPartFieldLabels[field], previousValue, newValue, `new-part-${field}`));
            }

            return parts;
        }, []);

        return (
            <>
                <span style={styles.editorName}>{editor}</span>
                <span> edited New Part <span style={styles.diffNeutral}>{this.getNewPartLabel(newItem || previousItem)}</span>{changes.length > 0 ? <>: {this.joinSentenceParts(changes, 'new-part-change')}</> : ''}</span>
            </>
        );
    }

    renderBomRevisionSentence(item, editor) {
        const previousItem = this.parseBomItem(item.previousBomItem);
        const newItem = this.parseBomItem(item.newBomItem);
        const action = this.safeString(item.bomAction).toUpperCase();

        if (action === 'ADD') {
            return (
                <>
                    <span style={styles.editorName}>{editor}</span>
                    <span> added BOM item <span style={styles.diffAdded}>{this.getBomLabel(newItem)}</span></span>
                </>
            );
        }

        if (action === 'DELETE') {
            return (
                <>
                    <span style={styles.editorName}>{editor}</span>
                    <span> removed BOM item <span style={styles.diffRemoved}>{this.getBomLabel(previousItem)}</span></span>
                </>
            );
        }

        const changes = Object.keys(bomFieldLabels).reduce((parts, field) => {
            const previousValue = previousItem ? this.safeString(previousItem[field]) : '';
            const newValue = newItem ? this.safeString(newItem[field]) : '';

            if (previousValue !== newValue) {
                parts.push(this.renderChangedField(bomFieldLabels[field], previousValue, newValue, `bom-${field}`));
            }

            return parts;
        }, []);

        return (
            <>
                <span style={styles.editorName}>{editor}</span>
                <span> updated BOM item <span style={styles.diffNeutral}>{this.getBomLabel(newItem || previousItem)}</span>{changes.length > 0 ? <>: {this.joinSentenceParts(changes, 'bom-change')}</> : ''}</span>
            </>
        );
    }

    renderTimelineItem(item, index, total) {
        const isDrawing = item.revisionType === 'DRAWING';
        const isBom = item.revisionType === 'BOM';
        const isNewPart = item.revisionType === 'NEW_PART';
        const previousFiles = this.parseDrawingFiles(item.previousDrawingFiles);
        const newFiles = this.parseDrawingFiles(item.newDrawingFiles);

        return (
            <div key={item.id} style={styles.timelineItem}>
                <div style={styles.timelineRail}>
                    <div style={isNewPart ? styles.newPartDot : isBom ? styles.bomDot : isDrawing ? styles.drawingDot : styles.partDot}>
                        {isNewPart ? <FiPlusSquare /> : isBom ? <FiList /> : isDrawing ? <FiFileText /> : <FiTag />}
                    </div>
                    {index < total - 1 && <div style={styles.timelineLine} />}
                </div>
                <div style={styles.timelineContent}>
                    <div style={styles.activityLine}>
                        <div style={styles.activitySentence}>
                            {this.renderRevisionSentence(item, previousFiles, newFiles)}
                            <span style={styles.metaDivider}> | </span>
                            <span style={styles.activityDate}>{moment(item.editedAt || item.createdAt).format('MMM D, YYYY HH:mm')}</span>
                        </div>
                        <span style={isNewPart ? styles.newPartBadge : isBom ? styles.bomBadge : isDrawing ? styles.drawingBadge : styles.partBadge}>
                            {revisionTypeLabels[item.revisionType] || item.revisionType}
                        </span>
                    </div>

                    {isDrawing && (
                        <div style={styles.fileComparison}>
                            {this.renderFileList('Previous drawing files', previousFiles)}
                            {this.renderFileList('Updated drawing files', newFiles)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderGroup(group) {
        const isExpanded = !!this.state.expandedGroups[group.key];
        const isNewPartGroup = group.kind === 'NEW_PART';
        const latestDate = group.latestEditedAt ? moment(group.latestEditedAt).format('YYYY-MM-DD HH:mm') : '-';
        const revisionCount = group.histories.length;

        return (
            <div key={group.key} style={styles.group}>
                <button
                    type="button"
                    style={styles.groupHeader}
                    onClick={() => this.toggleGroup(group.key)}
                >
                    <div style={styles.expandIcon}>
                        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                    </div>
                    <div style={styles.groupMain}>
                        <div style={styles.groupTitleRow}>
                            <span style={styles.groupTitle}>{group.currentPartNo}</span>
                            <span style={styles.revisionCount}>{revisionCount} revision{revisionCount === 1 ? '' : 's'}</span>
                        </div>
                        <div style={styles.groupMeta}>
                            {group.currentCustomer && <span>{group.currentCustomer}</span>}
                            {group.currentPartDesc && <span>{group.currentPartDesc}</span>}
                            <span>Latest edit: {latestDate}</span>
                        </div>
                    </div>
                    <div style={styles.currentDrawing}>
                        <div style={styles.currentLabel}>{isNewPartGroup ? 'Current New Part' : 'Current Drawing'}</div>
                        <div style={styles.currentValue}>
                            {isNewPartGroup ? `${group.currentNewPartType || '-'} / ${group.currentNewPartStatus || '-'}` : (
                                <>
                                    {group.currentDrawingNo}
                                    {group.currentDrawingRevision !== '-' ? ` / Rev ${group.currentDrawingRevision}` : ''}
                                </>
                            )}
                        </div>
                        <div style={styles.currentFiles}>
                            {isNewPartGroup ? (group.currentPartDesc || 'No description') : `${group.currentDrawingFiles.length} file${group.currentDrawingFiles.length === 1 ? '' : 's'}`}
                        </div>
                    </div>
                </button>

                {isExpanded && (
                    <div style={styles.groupBody}>
                        {isNewPartGroup ? (
                            <div style={styles.currentSnapshot}>
                                <div style={styles.snapshotItem}>
                                    <div style={styles.snapshotLabel}>Latest Current New Part</div>
                                    <div style={styles.snapshotValue}>{group.currentPartNo}</div>
                                </div>
                                <div style={styles.snapshotItem}>
                                    <div style={styles.snapshotLabel}>Part Type / Status</div>
                                    <div style={styles.snapshotValue}>{group.currentNewPartType || '-'} / {group.currentNewPartStatus || '-'}</div>
                                </div>
                                <div style={styles.snapshotItem}>
                                    <div style={styles.snapshotLabel}>Description</div>
                                    <div style={styles.snapshotValue}>{group.currentPartDesc || '-'}</div>
                                </div>
                            </div>
                        ) : (
                            <div style={styles.currentSnapshot}>
                                <div style={styles.snapshotItem}>
                                    <div style={styles.snapshotLabel}>Latest Current Part No</div>
                                    <div style={styles.snapshotValue}>{group.currentPartNo}</div>
                                </div>
                                <div style={styles.snapshotItem}>
                                    <div style={styles.snapshotLabel}>Latest Current Drawing</div>
                                    <div style={styles.snapshotValue}>
                                        {group.currentDrawingNo}
                                        {group.currentDrawingRevision !== '-' ? ` / Rev ${group.currentDrawingRevision}` : ''}
                                    </div>
                                </div>
                                <div style={styles.snapshotFiles}>
                                    {this.renderFileList('Current drawing files', group.currentDrawingFiles)}
                                </div>
                            </div>
                        )}
                        <div style={styles.timeline}>
                            {group.histories.map((item, index) => this.renderTimelineItem(item, index, group.histories.length))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!canAccessEngineeringModule(user)) {
            return <Navigate to="/" />;
        }

        const groups = this.getGroupedHistories();
        const totalPages = Math.max(1, Math.ceil(this.state.totalRows / this.state.rowsPerPage));

        return (
            <div style={styles.page}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>Revision History</h2>
                        <div style={styles.subtitle}>Current part and drawing records are grouped into expandable revision timelines.</div>
                    </div>
                    <Button
                        variant="outline-primary"
                        onClick={() => this.loadHistory()}
                        disabled={this.state.loading}
                        style={styles.refreshButton}
                    >
                        <FiRefreshCw /> Refresh
                    </Button>
                </div>

                <div style={styles.toolbar}>
                    <div style={styles.searchWrap}>
                        <FiSearch style={styles.searchIcon} />
                        <FormControl
                            type="text"
                            placeholder="Search part no, drawing no, revision, or editor"
                            value={this.state.keyword}
                            onChange={event => this.setState({ keyword: event.target.value })}
                            onKeyDown={event => {
                                if (event.key === 'Enter') this.loadHistory(1);
                            }}
                            style={styles.searchInput}
                        />
                    </div>
                    <Form.Control
                        as="select"
                        value={this.state.revisionType}
                        onChange={event => this.setState({ revisionType: event.target.value }, () => this.loadHistory(1))}
                        style={styles.typeSelect}
                    >
                        <option value="ALL">All revisions</option>
                        <option value="PART_NO">Part No</option>
                        <option value="DRAWING">Drawing</option>
                        <option value="BOM">BOM</option>
                        <option value="NEW_PART">New Part</option>
                    </Form.Control>
                    <Button variant="primary" onClick={() => this.loadHistory(1)}>
                        Search
                    </Button>
                </div>

                <div style={styles.groupList}>
                    {this.state.loading ? (
                        <div style={styles.emptyState}>Loading revision history...</div>
                    ) : groups.length === 0 ? (
                        <div style={styles.emptyState}>No revision history found.</div>
                    ) : (
                        groups.map(group => this.renderGroup(group))
                    )}
                </div>

                <div style={styles.pagination}>
                    <Button
                        variant="outline-secondary"
                        disabled={this.state.currentPage <= 1 || this.state.loading}
                        onClick={() => this.loadHistory(this.state.currentPage - 1)}
                    >
                        Previous
                    </Button>
                    <span style={styles.pageCount}>
                        Page {this.state.currentPage} of {totalPages}
                    </span>
                    <Button
                        variant="outline-secondary"
                        disabled={this.state.currentPage >= totalPages || this.state.loading}
                        onClick={() => this.loadHistory(this.state.currentPage + 1)}
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    }
}

const styles = {
    page: {
        maxWidth: '1180px',
        margin: '0 auto',
        padding: '8px 4px 32px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        marginBottom: '18px'
    },
    title: {
        margin: 0,
        fontSize: '28px',
        fontWeight: 700,
        color: '#1f2937'
    },
    subtitle: {
        marginTop: '6px',
        color: '#6b7280',
        fontSize: '14px'
    },
    refreshButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    toolbar: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '14px',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        background: '#ffffff',
        marginBottom: '18px'
    },
    searchWrap: {
        position: 'relative',
        flex: '1 1 360px'
    },
    searchIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6b7280'
    },
    searchInput: {
        paddingLeft: '38px'
    },
    typeSelect: {
        width: '180px'
    },
    groupList: {
        display: 'grid',
        gap: '12px'
    },
    group: {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        background: '#ffffff',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
    },
    groupHeader: {
        width: '100%',
        border: 'none',
        background: '#ffffff',
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr) minmax(220px, 320px)',
        gap: '12px',
        alignItems: 'center',
        padding: '16px',
        textAlign: 'left',
        cursor: 'pointer'
    },
    expandIcon: {
        color: '#4b5563',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px'
    },
    groupMain: {
        minWidth: 0
    },
    groupTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap'
    },
    groupTitle: {
        color: '#111827',
        fontSize: '17px',
        fontWeight: 700,
        wordBreak: 'break-word'
    },
    revisionCount: {
        color: '#4338ca',
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: '999px',
        padding: '3px 9px',
        fontSize: '12px',
        fontWeight: 700
    },
    groupMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 12px',
        color: '#6b7280',
        fontSize: '13px',
        marginTop: '6px'
    },
    currentDrawing: {
        borderLeft: '1px solid #e5e7eb',
        paddingLeft: '14px',
        minWidth: 0
    },
    currentLabel: {
        color: '#6b7280',
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'uppercase'
    },
    currentValue: {
        color: '#111827',
        fontSize: '14px',
        fontWeight: 700,
        marginTop: '3px',
        wordBreak: 'break-word'
    },
    currentFiles: {
        color: '#6b7280',
        fontSize: '12px',
        marginTop: '3px'
    },
    groupBody: {
        borderTop: '1px solid #e5e7eb',
        padding: '16px',
        background: '#fbfcfe'
    },
    currentSnapshot: {
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 1fr) minmax(180px, 1fr) minmax(260px, 2fr)',
        gap: '12px',
        marginBottom: '18px',
        alignItems: 'start'
    },
    snapshotItem: {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        background: '#ffffff'
    },
    snapshotLabel: {
        color: '#6b7280',
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '6px'
    },
    snapshotValue: {
        color: '#111827',
        fontSize: '15px',
        fontWeight: 700,
        wordBreak: 'break-word'
    },
    snapshotFiles: {
        minWidth: 0
    },
    timeline: {
        position: 'relative'
    },
    timelineItem: {
        display: 'grid',
        gridTemplateColumns: '42px minmax(0, 1fr)',
        gap: '12px',
        marginBottom: '14px'
    },
    timelineRail: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center'
    },
    timelineLine: {
        position: 'absolute',
        top: '34px',
        bottom: '-16px',
        width: '2px',
        background: '#d1d5db'
    },
    partDot: {
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        background: '#eef2ff',
        color: '#4338ca',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #c7d2fe',
        zIndex: 1
    },
    drawingDot: {
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        background: '#ecfdf5',
        color: '#047857',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #a7f3d0',
        zIndex: 1
    },
    bomDot: {
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        background: '#fff7ed',
        color: '#c2410c',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #fed7aa',
        zIndex: 1
    },
    newPartDot: {
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        background: '#eff6ff',
        color: '#1d4ed8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #bfdbfe',
        zIndex: 1
    },
    timelineContent: {
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '16px'
    },
    activityLine: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        alignItems: 'flex-start'
    },
    activitySentence: {
        color: '#111827',
        fontSize: '16px',
        lineHeight: 1.5,
        wordBreak: 'break-word'
    },
    editorName: {
        color: '#111827',
        fontWeight: 700
    },
    activityDate: {
        color: '#6b7280',
        fontSize: '14px',
        whiteSpace: 'nowrap'
    },
    diffRemoved: {
        color: '#b42318',
        fontWeight: 700
    },
    diffAdded: {
        color: '#087443',
        fontWeight: 700
    },
    diffNeutral: {
        color: '#374151',
        fontWeight: 700
    },
    metaDivider: {
        color: '#d1d5db'
    },
    partBadge: {
        background: '#eef2ff',
        color: '#4338ca',
        border: '1px solid #c7d2fe',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap'
    },
    drawingBadge: {
        background: '#ecfdf5',
        color: '#047857',
        border: '1px solid #a7f3d0',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap'
    },
    bomBadge: {
        background: '#fff7ed',
        color: '#c2410c',
        border: '1px solid #fed7aa',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap'
    },
    newPartBadge: {
        background: '#eff6ff',
        color: '#1d4ed8',
        border: '1px solid #bfdbfe',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap'
    },
    fileComparison: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '12px',
        marginTop: '14px'
    },
    fileColumn: {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        background: '#ffffff'
    },
    fileColumnTitle: {
        color: '#374151',
        fontWeight: 700,
        fontSize: '13px',
        marginBottom: '8px'
    },
    fileLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '7px 0',
        color: '#2563eb',
        wordBreak: 'break-word'
    },
    emptyFiles: {
        color: '#9ca3af',
        fontSize: '13px'
    },
    emptyState: {
        padding: '36px',
        textAlign: 'center',
        border: '1px dashed #d1d5db',
        borderRadius: '8px',
        color: '#6b7280',
        background: '#ffffff'
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        marginTop: '18px'
    },
    pageCount: {
        color: '#4b5563',
        fontWeight: 600
    }
};